/**
 * TokenBalanceCard.tsx
 *
 * Displays creator's or investor's token balance and ownership percentage.
 * Fetches from GET /api/algorand-status.
 *
 * User-facing: plain English only — no Islamic finance terms.
 * Shows: "Your Ownership", "Usage Fee", "Ownership Purchase"
 */

import { useState, useEffect } from 'react';
import { Coins, TrendingUp, Shield, Building2 } from 'lucide-react';

interface Allocation {
  userId: string;
  holderType: string;
  tokensHeld: number;
  ownershipPct: number;
}

interface TokenStatus {
  asaId: number;
  asaName: string;
  status: string;
  unitPrice: number;
  allocations: Allocation[];
  totalBurned: number;
  creatorOwnershipPct: number;
  monthsRemaining: number | null;
  llcName: string | null;
}

interface TokenBalanceCardProps {
  campaignId: string;
  userId: string;
  viewAs: 'creator' | 'investor';
}

export default function TokenBalanceCard({ campaignId, userId, viewAs }: TokenBalanceCardProps) {
  const [status, setStatus] = useState<TokenStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function fetchStatus() {
      try {
        const res = await fetch(`/api/algorand-status?campaignId=${campaignId}`);
        if (!res.ok) {
          if (res.status === 404) {
            setStatus(null);
            return;
          }
          throw new Error('Failed to load token status');
        }
        const data = await res.json();
        setStatus(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Unknown error');
      } finally {
        setLoading(false);
      }
    }
    fetchStatus();
  }, [campaignId]);

  if (loading) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-6 animate-pulse">
        <div className="h-6 bg-gray-200 rounded w-1/3 mb-4" />
        <div className="h-20 bg-gray-100 rounded mb-3" />
        <div className="h-4 bg-gray-200 rounded w-1/2" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-xl border border-red-200 p-6">
        <p className="text-red-600 text-sm">{error}</p>
      </div>
    );
  }

  if (!status) {
    return null; // No tokens minted yet
  }

  // Find the user's allocation
  const myAllocation = status.allocations.find(a => a.userId === userId);
  const ownershipPct = viewAs === 'creator'
    ? status.creatorOwnershipPct
    : (myAllocation?.ownershipPct ?? 0);
  const tokensHeld = myAllocation?.tokensHeld ?? 0;

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-indigo-600 to-indigo-700 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-white">
            <Coins className="w-5 h-5" />
            <span className="font-semibold">Your Ownership</span>
          </div>
          <span className="text-indigo-200 text-sm">{status.asaName}</span>
        </div>
      </div>

      {/* Ownership progress */}
      <div className="px-6 py-5">
        <div className="flex items-end justify-between mb-2">
          <span className="text-4xl font-bold text-gray-900">
            {(ownershipPct * 100).toFixed(1)}%
          </span>
          <span className="text-sm text-gray-500">
            {tokensHeld.toLocaleString()} / 1,000 tokens
          </span>
        </div>

        {/* Progress bar */}
        <div className="w-full bg-gray-100 rounded-full h-3 mb-4">
          <div
            className="bg-indigo-600 h-3 rounded-full transition-all duration-500"
            style={{ width: `${Math.min(ownershipPct * 100, 100)}%` }}
          />
        </div>

        {/* Stats grid */}
        <div className="grid grid-cols-2 gap-4 mt-4">
          <div className="bg-gray-50 rounded-lg p-3">
            <div className="flex items-center gap-2 mb-1">
              <TrendingUp className="w-4 h-4 text-green-600" />
              <span className="text-xs text-gray-500 uppercase">Tokens Redeemed</span>
            </div>
            <span className="text-lg font-semibold text-gray-900">
              {status.totalBurned.toLocaleString()}
            </span>
          </div>

          {status.monthsRemaining !== null && (
            <div className="bg-gray-50 rounded-lg p-3">
              <div className="flex items-center gap-2 mb-1">
                <Shield className="w-4 h-4 text-indigo-600" />
                <span className="text-xs text-gray-500 uppercase">Months Remaining</span>
              </div>
              <span className="text-lg font-semibold text-gray-900">
                {status.monthsRemaining}
              </span>
            </div>
          )}
        </div>

        {/* Token value */}
        <div className="mt-4 pt-4 border-t border-gray-100">
          <div className="flex justify-between text-sm">
            <span className="text-gray-500">Token Value</span>
            <span className="text-gray-900 font-medium">
              ${status.unitPrice.toFixed(2)} / token
            </span>
          </div>
          <div className="flex justify-between text-sm mt-1">
            <span className="text-gray-500">Your Equity Value</span>
            <span className="text-gray-900 font-medium">
              ${(tokensHeld * status.unitPrice).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </span>
          </div>
        </div>

        {/* LLC info */}
        {status.llcName && (
          <div className="mt-4 pt-4 border-t border-gray-100">
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <Building2 className="w-4 h-4" />
              <span>{status.llcName}</span>
            </div>
          </div>
        )}

        {/* Status badge */}
        <div className="mt-4 flex justify-end">
          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
            status.status === 'active' ? 'bg-green-100 text-green-800' :
            status.status === 'completed' ? 'bg-indigo-100 text-indigo-800' :
            status.status === 'minted' ? 'bg-yellow-100 text-yellow-800' :
            'bg-gray-100 text-gray-800'
          }`}>
            {status.status === 'active' ? 'Active' :
             status.status === 'completed' ? 'Fully Owned' :
             status.status === 'minted' ? 'Processing' :
             status.status}
          </span>
        </div>
      </div>
    </div>
  );
}
